import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DividerModule } from '@openng/optimus-ui/divider';
import { debounceTime } from 'rxjs/operators';

import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { fb, routeParams } from '../../../../../modules/shared/shared.global-service';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { FieldErrorFactory } from '../../../../../modules/shared/service/field-error.service';
import config from '../../../../../config/config';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { projectMutationFrag } from './organization-settings-project-dialog.query';
import { ProjectObject } from '@libs/types/src/mado/object-type/organization';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { OrganizationSettingsProjectDialogStore } from './organization-settings-project-dialog.store';

@Component({
    selector: 'app-organization-settings-project-dialog',
    templateUrl: './organization-settings-project-dialog.component.html',
    providers: [OrganizationSettingsProjectDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        DividerModule,
        TextInputComponent,
    ]
})
export class OrganizationSettingsProjectDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(OrganizationSettingsProjectDialogStore);

    protected readonly data = this.dialogConfig.data.project || null;

    protected form = fb.group({
        id: fb.control<number | null>(null),
        name: fb.control<string | null>(null, [Validators.required]),
        code: fb.control<string | null>(null, [Validators.required]),
        description: fb.control<string | null>(null),
        dateFrom: fb.control<Date | null>(null, [Validators.required]),
        dateTo: fb.control<Date | null>(null, [this.dateFromBeforeDateToValidator()]),
    })

    protected readonly prefix = 'organization-settings.mado.project.';

    constructor() {
        super();
        this.subs.push(
            this.form.controls.dateTo.valueChanges.pipe(debounceTime(config.debounceTime))
                .subscribe((dateFrom) => {
                if (dateFrom && this.form.value.dateTo) {
                    this.form.controls.dateTo.markAsTouched();
                    this.form.controls.dateTo.updateValueAndValidity();
                }
            }),
        );

        effect(() => {
            this.patchForm(this.data);
        })
    }

    override async save() {
        if (isInvalid(this.form) || !this.isDateToValid()) return;
        
        const data = {
            name: this.form.value.name!,
            code: this.form.value.code!,
            description: this.form.value.description,
            dateFrom: this.form.value.dateFrom!.toISOString(),
            dateTo: this.form.value.dateTo ? this.form.value.dateTo!.toISOString() : null,
        }

        if (this.form.value.id) {
            await this.store.api.updateProject(
                {
                    id: this.form.value.id,
                    ...data,
                },
                projectMutationFrag,
            )
            contentGlobalSuccess.next('common.updated')
            this.dialogRef.close(true);

        } else if (routeParams.organizationId()) {
            await this.store.api.createProject(
                {
                    organizationId: Number(routeParams.organizationId()),
                    ...data,
                },
                projectMutationFrag,
            )
            contentGlobalSuccess.next('common.created')
            this.dialogRef.close(true);
        }
    }

    private patchForm(item: ProjectObject | null) {
        if (item) {
            this.form.patchValue({
                id: item.id,
                name: item.name,
                code: item.code,
                description: item.description,
                dateFrom: new Date(item.dateFrom),
                dateTo: item.dateTo ? new Date(item.dateTo) : null,
            })
        }
    }

    // todo zkontrolovat, proč nefunguje form validator
    private isDateToValid() {        
        if (!this.form.value.dateTo) return true

        if (this.form.value.dateTo && this.form.value.dateFrom && this.form.value.dateTo > this.form.value.dateFrom) {
            return true
        }

        return false
    }

    private dateFromBeforeDateToValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value && this.form?.value?.dateFrom && control.value < this.form.value.dateFrom) {
                return FieldErrorFactory.invalidValidityPeriodError();
            }
            return null;
        };
    }
}
